import type { ComponentProps } from 'react';

import { Col } from '@opendoor/atoms/box';
import { determinePropertyActionability } from 'components/marketplace/helpers/propertyActionability';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { isComingSoonProperty } from 'components/properties/helpers/property';

import { BuyDirectCTA } from '../BuyDirectCTA';
import { BuyWithConfidence } from '../BuyWithConfidence';
import { AvailableSoonNotifyCard } from './AvailableSoonNotifyCard';
import type { CertaintyTreatment } from './CertaintyPanel';
import { GuaranteeCard } from './CertaintyPanel/GuaranteeCard';

type PropertyCTAsData = ComponentProps<typeof BuyDirectCTA>['property'] &
  Pick<MarketplaceProperty, 'estimatedMonthlyPayment' | 'listPrice' | 'shouldShowGuarantee'>;

interface PropertyCTAsProps {
  property: PropertyCTAsData;
  // Certainty-panel treatment only; null renders the unchanged control page.
  certainty?: CertaintyTreatment | null;
  // Available-soon treatment only: fills the empty CTA slot with the
  // notify-me card on coming-soon PDPs.
  availableSoonNotifyEnabled?: boolean;
}

export const PropertyCTAs = ({
  property,
  certainty = null,
  availableSoonNotifyEnabled = false,
}: PropertyCTAsProps) => {
  const { isActionable, tourKind } = determinePropertyActionability(property);

  if (!isActionable || !tourKind) {
    // Coming-soon homes are never actionable; the card fills a slot that
    // otherwise renders empty. Keyed so a client-side PDP-to-PDP navigation
    // resets the card's state for the new home.
    if (availableSoonNotifyEnabled && isComingSoonProperty(property)) {
      return (
        <Col className="gap-12 px-0 pt-12">
          <AvailableSoonNotifyCard key={property.addressToken} property={property} />
        </Col>
      );
    }
    return null;
  }

  const showGuaranteeCard = certainty?.showGuaranteeCard ?? false;

  return (
    <Col className="gap-12 px-0 pt-12">
      <BuyDirectCTA
        property={property}
        certaintyStats={certainty?.buyBoxStats ?? null}
        hideTrustBadges={showGuaranteeCard}
      />
      {property.shouldShowGuarantee &&
        (certainty ? (
          <GuaranteeCard
            addressToken={property.addressToken}
            showMortgageCta={certainty.showMortgageCta}
          />
        ) : (
          <BuyWithConfidence />
        ))}
    </Col>
  );
};
